import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';

// The owner's word on whether the people this ad brought were real customers. Cheap results that never buy
// aren't a win, so the system learns from this as much as from the numbers.
export default function CustomerFeedback({ campaign, onChange }) {
  const [picked, setPicked] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const ran = ['live', 'paused', 'ended'].includes(campaign.status);
  const results = campaign.performance?.results || 0;
  if (!ran || (!results && campaign.status !== 'ended')) return null;

  const given = campaign.customer_feedback;
  if (given) {
    return (
      <p className="p-3 rounded-xl bg-card border-2 border-ink/20 text-xs text-muted-foreground flex items-center gap-2" data-testid="feedback-given">
        {given.good ? <ThumbsUp className="w-4 h-4 text-money" aria-hidden="true" /> : <ThumbsDown className="w-4 h-4 text-amber-700 dark:text-amber-400" aria-hidden="true" />}
        Thanks! You said these {given.good ? 'were' : "weren't"} real customers. Your next ads learn from it.
      </p>
    );
  }

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/api/campaigns/${campaign.id}/feedback`, { good: picked, note });
      onChange(data);
      toast.success('Thanks for telling us!');
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Couldn't save that");
    }
    setBusy(false);
  };

  return (
    <div className="p-4 rounded-xl bg-card border-2 border-ink space-y-3" data-testid="customer-feedback">
      <div>
        <h3 className="text-sm font-semibold">Were these real customers?</h3>
        <p className="text-xs text-muted-foreground">Did the people who {campaign.status === 'ended' ? 'came from this ad' : 'are coming from this ad'} actually want to buy? Your answer helps us make better ads for you.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant={picked === true ? 'default' : 'outline'} className={picked === true ? 'gradient-bg' : ''}
          onClick={() => setPicked(true)} data-testid="feedback-good">
          <ThumbsUp className="w-3.5 h-3.5 mr-1" /> Yes, real customers
        </Button>
        <Button size="sm" variant={picked === false ? 'default' : 'outline'} className={picked === false ? 'gradient-bg' : ''}
          onClick={() => setPicked(false)} data-testid="feedback-bad">
          <ThumbsDown className="w-3.5 h-3.5 mr-1" /> Mostly not
        </Button>
      </div>
      {picked !== null && (
        <>
          <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} maxLength={500}
            placeholder={picked ? 'Anything that worked well? (optional)' : 'What went wrong? e.g. wrong area, only asking prices (optional)'}
            aria-label="Anything to add" />
          <Button size="sm" className="gradient-bg" onClick={send} disabled={busy} data-testid="feedback-send">
            {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Send
          </Button>
        </>
      )}
    </div>
  );
}
